/**
 * DownlineManagerView — Direct-child compensation manager.
 *
 * Shows the caller's DIRECT children only (no skip-level).
 * Allows: create new Schedule A (proposes; approval required unless super_admin),
 *         propose a change to an existing one.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit2, X } from 'lucide-react';
import api from '../../lib/api';

export default function DownlineManagerView() {
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // { child, existing }

  const load = async () => {
    try {
      const d = await api.getScheduleADownlineManager();
      setChildren(d.children || []);
    } catch (e) {
      toast.error('Failed to load downline');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openDialog = (child, existing) => setDialog({ child, existing });
  const closeDialog = () => setDialog(null);

  if (loading) return <div className="text-sm text-muted-foreground" data-testid="downline-loading">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="downline-manager-view">
      <Card>
        <CardHeader>
          <CardTitle>Direct Downline ({children.length})</CardTitle>
          <p className="text-xs text-muted-foreground">
            You can only assign compensation to your immediate children — no skip-level.
          </p>
        </CardHeader>
        <CardContent>
          {children.length === 0 && (
            <p className="text-sm text-muted-foreground py-8 text-center" data-testid="downline-empty">
              No direct-report users yet. Invite an agent from Team & Users to get started.
            </p>
          )}
          <div className="space-y-2">
            {children.map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between p-3 border rounded-lg"
                data-testid={`downline-child-${c.id}`}
              >
                <div>
                  <div className="font-medium">
                    {(c.first_name || '') + ' ' + (c.last_name || '')}
                    <span className="text-xs text-muted-foreground ml-2">{c.email}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">{c.role?.replace('_', ' ')}</div>
                </div>
                <div className="flex items-center gap-2">
                  {c.current_schedule_a ? (
                    <>
                      <Badge variant={c.has_schedule_a ? 'default' : 'secondary'}>
                        {c.current_schedule_a.compensation_type === 'fixed'
                          ? `${c.current_schedule_a.split_percent}% Fixed`
                          : 'Tiered'}
                        {' '}(v{c.current_schedule_a.version})
                      </Badge>
                      {c.has_pending_change && (
                        <Badge variant="outline" className="border-amber-500 text-amber-600">
                          Pending Change
                        </Badge>
                      )}
                    </>
                  ) : (
                    <Badge variant="outline">No Schedule A</Badge>
                  )}
                  <Button
                    size="sm"
                    variant={c.current_schedule_a ? 'outline' : 'default'}
                    onClick={() => openDialog(c, c.current_schedule_a)}
                    data-testid={`downline-edit-${c.id}`}
                  >
                    {c.current_schedule_a ? <><Edit2 className="h-3 w-3 mr-1" /> Propose Change</> :
                      <><Plus className="h-3 w-3 mr-1" /> Assign Schedule A</>}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {dialog && (
        <CompensationDialog
          child={dialog.child}
          existing={dialog.existing}
          onClose={closeDialog}
          onSaved={() => { closeDialog(); load(); }}
        />
      )}
    </div>
  );
}

function CompensationDialog({ child, existing, onClose, onSaved }) {
  const [compType, setCompType] = useState(existing?.compensation_type || 'fixed');
  const [splitPct, setSplitPct] = useState(existing?.split_percent || 50);
  const [tiers, setTiers] = useState([
    { min_volume: 0, max_volume: 500000, split_percent: 50 },
    { min_volume: 500000, max_volume: 1000000, split_percent: 60 },
    { min_volume: 1000000, max_volume: null, split_percent: 70 },
  ]);
  const [tierVolMethod, setTierVolMethod] = useState('CURRENT_MONTH');
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const isEdit = !!existing;

  const submit = async () => {
    if (compType === 'fixed' && (splitPct < 0 || splitPct > 100)) {
      toast.error('Split must be between 0 and 100');
      return;
    }
    if (compType === 'tiered') {
      for (const t of tiers) {
        if (t.split_percent < 0 || t.split_percent > 100) {
          toast.error('Every tier split_percent must be 0–100');
          return;
        }
      }
    }
    setSaving(true);
    try {
      const schedule_a = compType === 'fixed'
        ? { compensation_type: 'fixed', split_percent: parseFloat(splitPct) }
        : { compensation_type: 'tiered', tiers: tiers.map(t => ({
            min_volume: parseFloat(t.min_volume),
            max_volume: t.max_volume === null || t.max_volume === '' ? null : parseFloat(t.max_volume),
            split_percent: parseFloat(t.split_percent),
          })), tier_volume_method: tierVolMethod };
      if (isEdit) {
        await api.proposeScheduleAChange(existing.relationship_id, {
          schedule_a, effective_date: effectiveDate, note,
        });
        toast.success('Change proposed. Awaiting approval.');
      } else {
        await api.createScheduleARelationship({
          child_user_id: child.id, schedule_a, effective_date: effectiveDate, note,
        });
        toast.success('Schedule A created.');
      }
      onSaved();
    } catch (e) {
      toast.error(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="compensation-dialog">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Propose Schedule A Change' : 'Assign Schedule A'} — {child.first_name} {child.last_name}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Proposing a change creates a new version pending approval from your upline. The current version stays active until approved.'
              : 'Your upline must approve this new compensation agreement.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Compensation Type</Label>
            <Select value={compType} onValueChange={setCompType}>
              <SelectTrigger data-testid="comp-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="fixed">Fixed % of Direct Upline Residual</SelectItem>
                <SelectItem value="tiered">Tiered (by qualifying volume)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {compType === 'fixed' && (
            <div>
              <Label>Your Split (% of parent&apos;s residual, 0–100)</Label>
              <Input
                type="number" min={0} max={100} step={0.01} value={splitPct}
                onChange={(e) => setSplitPct(e.target.value)}
                data-testid="split-pct-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Hard limit: no child may receive more than 100% of the parent&apos;s residual.
              </p>
            </div>
          )}

          {compType === 'tiered' && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Volume Tiers</Label>
                <Select value={tierVolMethod} onValueChange={setTierVolMethod}>
                  <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CURRENT_MONTH">Current Month (default)</SelectItem>
                    <SelectItem value="TRAILING_3_MONTHS">Trailing 3 Months</SelectItem>
                    <SelectItem value="YTD">Year-to-Date</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                {tiers.map((t, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-4">
                      <Label className="text-xs">Min Volume</Label>
                      <Input
                        type="number" value={t.min_volume}
                        onChange={(e) => {
                          const nt = [...tiers]; nt[i].min_volume = e.target.value; setTiers(nt);
                        }}
                      />
                    </div>
                    <div className="col-span-4">
                      <Label className="text-xs">Max Volume (blank = ∞)</Label>
                      <Input
                        type="number" value={t.max_volume ?? ''}
                        onChange={(e) => {
                          const nt = [...tiers]; nt[i].max_volume = e.target.value || null; setTiers(nt);
                        }}
                      />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">Split %</Label>
                      <Input
                        type="number" min={0} max={100} step={0.01} value={t.split_percent}
                        onChange={(e) => {
                          const nt = [...tiers]; nt[i].split_percent = e.target.value; setTiers(nt);
                        }}
                      />
                    </div>
                    <Button
                      variant="ghost" size="icon" className="col-span-1"
                      onClick={() => setTiers(tiers.filter((_, j) => j !== i))}
                      disabled={tiers.length <= 1}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline" size="sm"
                  onClick={() => setTiers([...tiers, { min_volume: 0, max_volume: null, split_percent: 50 }])}
                >
                  <Plus className="h-3 w-3 mr-1" /> Add Tier
                </Button>
              </div>
            </div>
          )}

          <div>
            <Label>Effective Date</Label>
            <Input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Context for the approver" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="compensation-save-btn">
            {saving ? 'Saving…' : isEdit ? 'Propose Change' : 'Assign & Submit for Approval'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
